import React, { useState } from "react";
import Layout from "../../layout/Layout";
import {
  Button,
  Card,
  Badge,
  Select,
  Modal,
  Form,
  Input,
  Table,
  Space,
  Typography,
  Statistic,
  Row,
  Col,
  Tag,
  Popconfirm,
  // ColorPicker,
  // App
} from "antd";
import {
  PlusOutlined,
  TabletOutlined,
  MonitorOutlined,
  WifiOutlined,
  EditOutlined,
  DeleteOutlined,
  PoweroffOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined
} from "@ant-design/icons";

const { Title, Paragraph } = Typography;
const { Option } = Select;

const KdsDeviceManagement = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [form] = Form.useForm();
  // const { message } = App.useApp();

  // Mock data
  const [devices, setDevices] = useState([
    {
      id: 'KDS-001',
      name: 'Grill KDS #1',
      deviceId: 'GRL-KDS-001',
      macAddress: '00:1B:44:11:3A:B7',
      assignedStation: 'Grill Station',
      displayColor: '#EF4444',
      displayLabel: 'GRILL 1',
      deviceType: 'standard',
      status: 'online',
      lastSync: '2024-01-15T10:30:00Z',
      lastHeartbeat: '2024-01-15T10:29:45Z',
      isActive: true
    },
    {
      id: 'KDS-002',
      name: 'Cold KDS #1',
      deviceId: 'CLD-KDS-001',
      macAddress: '00:1B:44:11:3A:B8',
      assignedStation: 'Cold Station',
      displayColor: '#3B82F6',
      displayLabel: 'COLD 1',
      deviceType: 'standard',
      status: 'online',
      lastSync: '2024-01-15T10:29:30Z',
      lastHeartbeat: '2024-01-15T10:29:30Z',
      isActive: true
    },
    {
      id: 'KDS-003',
      name: 'Expeditor KDS',
      deviceId: 'EXP-KDS-001',
      macAddress: '00:1B:44:11:3A:B9',
      assignedStation: 'Expeditor',
      displayColor: '#10B981',
      displayLabel: 'EXPO',
      deviceType: 'expeditor',
      status: 'online',
      lastSync: '2024-01-15T10:28:15Z',
      lastHeartbeat: '2024-01-15T10:29:00Z',
      isActive: true
    },
    {
      id: 'KDS-004',
      name: 'Prep KDS #1',
      deviceId: 'PREP-KDS-001',
      macAddress: '00:1B:44:11:3A:C0',
      displayColor: '#F59E0B',
      displayLabel: 'PREP 1',
      deviceType: 'standard',
      status: 'offline',
      lastSync: '2024-01-15T09:45:00Z',
      lastHeartbeat: '2024-01-15T09:45:00Z',
      isActive: false
    }
  ]);

  const mockStations = [
    { id: '1', name: 'Grill Station' },
    { id: '2', name: 'Cold Station' },
    { id: '3', name: 'Expeditor' },
    { id: '4', name: 'Beverage Station' },
    { id: '5', name: 'Prep Station' }
  ];

  const handleSubmit = (values) => {
    if (editingDevice) {
      setDevices(devices.map(d => d.id === editingDevice.id ? {
        ...d,
        ...values,
        assignedStation: values.assignedStation === 'unassigned' ? undefined : values.assignedStation,
        isActive: true,
        status: 'online',
        lastSync: new Date().toISOString(),
        lastHeartbeat: new Date().toISOString()
      } : d));
      // message.success('Device updated successfully');
    } else {
      const newDevice = {
        id: `KDS-${String(devices.length + 1).padStart(3, '0')}`,
        ...values,
        assignedStation: values.assignedStation === 'unassigned' ? undefined : values.assignedStation,
        isActive: true,
        status: 'online',
        lastSync: new Date().toISOString(),
        lastHeartbeat: new Date().toISOString()
      };
      setDevices([...devices, newDevice]);
      // message.success('Device registered successfully');
    }
    
    resetForm();
  };

  const resetForm = () => {
    form.resetFields();
    setEditingDevice(null);
    setIsModalVisible(false);
  };

  const handleEdit = (device) => {
    setEditingDevice(device);
    form.setFieldsValue({
      name: device.name,
      deviceId: device.deviceId,
      macAddress: device.macAddress || '',
      assignedStation: device.assignedStation || 'unassigned',
      displayColor: device.displayColor,
      displayLabel: device.displayLabel,
      deviceType: device.deviceType
    });
    setIsModalVisible(true);
  };

  const toggleDeviceStatus = (deviceId) => {
    setDevices(devices.map(d => d.id === deviceId ? {
      ...d,
      isActive: !d.isActive,
      status: d.isActive ? 'disabled' : 'online'
    } : d));
    // message.success('Device status updated');
  };

  const deleteDevice = (deviceId) => {
    setDevices(devices.filter(d => d.id !== deviceId));
    // message.success('Device deleted successfully');
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'online':
        return <WifiOutlined style={{ color: '#52c41a' }} />;
      case 'offline':
        return <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />;
      case 'disabled':
        return <PoweroffOutlined style={{ color: '#d9d9d9' }} />;
      default:
        return <WarningOutlined style={{ color: '#fa8c16' }} />;
    }
  };

  const getDeviceTypeColor = (type) => {
    switch (type) {
      case 'expeditor':
        return 'purple';
      case 'shared':
        return 'blue';
      default:
        return 'default';
    }
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'Never';
    const now = new Date();
    const time = new Date(timestamp);
    const diffInMinutes = Math.floor((now.getTime() - time.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const columns = [
    {
      title: 'Device',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div>
          <Space>
            <div 
              style={{
                width: 16,
                height: 16,
                borderRadius: '50%',
                backgroundColor: record.displayColor,
                border: '2px solid white',
                boxShadow: '0 0 0 1px #d9d9d9'
              }}
            />
            <div>
              <div style={{ fontWeight: 500 }}>{text}</div>
              <div style={{ fontSize: 12, color: '#666' }}>{record.deviceId}</div>
              {record.macAddress && (
                <div style={{ fontSize: 10, color: '#999', fontFamily: 'monospace' }}>
                  {record.macAddress}
                </div>
              )}
            </div>
          </Space>
        </div>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'deviceType',
      key: 'deviceType',
      render: (type) => (
        <Tag color={getDeviceTypeColor(type)} style={{ textTransform: 'capitalize' }}>
          {type}
        </Tag>
      ),
    },
    {
      title: 'Assigned Station',
      dataIndex: 'assignedStation',
      key: 'assignedStation',
      render: (station, record) => (
        station ? (
          <div>
            <div style={{ fontSize: 14 }}>{station}</div>
            <Tag size="small" color="blue">
              {record.displayLabel}
            </Tag>
          </div>
        ) : (
          <span style={{ color: '#999', fontSize: 14 }}>Unassigned</span>
        )
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Space>
          {getStatusIcon(status)}
          <span style={{ 
            textTransform: 'capitalize',
            color: status === 'online' ? '#52c41a' : 
                   status === 'offline' ? '#ff4d4f' : '#d9d9d9'
          }}>
            {status}
          </span>
        </Space>
      ),
    },
    {
      title: 'Last Sync',
      dataIndex: 'lastSync',
      key: 'lastSync',
      render: (timestamp) => (
        <Space size={4}>
          <ClockCircleOutlined style={{ fontSize: 12, color: '#666' }} />
          <span style={{ fontSize: 12, color: '#666' }}>{formatTimeAgo(timestamp)}</span>
        </Space>
      ),
    },
    {
      title: 'Heartbeat',
      dataIndex: 'lastHeartbeat',
      key: 'lastHeartbeat',
      render: (timestamp) => (
        <Space size={4}>
          <ClockCircleOutlined style={{ fontSize: 12, color: '#666' }} />
          <span style={{ fontSize: 12, color: '#666' }}>{formatTimeAgo(timestamp)}</span>
        </Space>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Button
            type="text"
            size="small"
            icon={<PoweroffOutlined />}
            onClick={() => toggleDeviceStatus(record.id)}
            style={{ color: record.isActive ? '#ff4d4f' : '#52c41a' }}
          />
          <Popconfirm
            title="Delete Device"
            description="Are you sure you want to delete this device?"
            onConfirm={() => deleteDevice(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="text"
              size="small"
              icon={<DeleteOutlined />}
              danger
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Layout>
      <div style={{ padding: "0 24px" }}>
        {/* Header */}
        <div style={{ marginBottom: "24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <Title level={2}>KDS Device Management</Title>
            <Paragraph>Register and manage KDS tablets and their connections</Paragraph>
          </div>
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={() => setIsModalVisible(true)}
          >
            Register Device
          </Button>
        </div>

        {/* Summary Cards */}
        <Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Devices"
                value={devices.length}
                prefix={<TabletOutlined style={{ color: '#2563EB' }} />}
              />
            </Card>
          </Col>
          
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Online Devices"
                value={devices.filter(d => d.status === 'online').length}
                valueStyle={{ color: '#52c41a' }}
                prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
              />
            </Card>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Assigned Devices"
                value={devices.filter(d => d.assignedStation).length}
                valueStyle={{ color: '#2563EB' }}
                prefix={<MonitorOutlined style={{ color: '#2563EB' }} />}
              />
            </Card>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Offline Devices"
                value={devices.filter(d => d.status === 'offline' || d.status === 'disabled').length}
                valueStyle={{ color: '#ff4d4f' }}
                prefix={<WarningOutlined style={{ color: '#ff4d4f' }} />}
              />
            </Card>
          </Col>
        </Row>

        {/* Devices Table */}
        <Card 
          title="Registered KDS Devices"
          extra={<span style={{ color: '#666' }}>Monitor and manage all registered KDS devices in your system</span>}
        >
          <Table
            columns={columns}
            dataSource={devices}
            rowKey="id"
            pagination={false}
          />
        </Card>

        {/* Add/Edit Device Modal */}
        <Modal
          title={editingDevice ? 'Edit KDS Device' : 'Register New KDS Device'}
          open={isModalVisible}
          onCancel={resetForm}
          footer={null}
          width={600}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              deviceType: 'standard',
              assignedStation: 'unassigned',
              displayColor: '#3B82F6'
            }}
          >
            <Form.Item
              name="name"
              label="Device Name"
              rules={[{ required: true, message: 'Please enter device name' }]}
            >
              <Input placeholder="Enter device name (e.g., 'Grill KDS #1')" />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="deviceId"
                  label="Device ID"
                  rules={[{ required: true, message: 'Please enter device ID' }]}
                >
                  <Input placeholder="Device identifier" />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  name="macAddress"
                  label="MAC Address (Optional)"
                >
                  <Input placeholder="00:1B:44:11:3A:B7" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="deviceType"
                  label="Device Type"
                  rules={[{ required: true, message: 'Please select device type' }]}
                >
                  <Select placeholder="Select device type">
                    <Option value="standard">Standard</Option>
                    <Option value="expeditor">Expeditor</Option>
                    <Option value="shared">Shared View</Option>
                  </Select>
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  name="assignedStation"
                  label="Assigned Station"
                >
                  <Select placeholder="Select station">
                    <Option value="unassigned">No Assignment</Option>
                    {mockStations.map((station) => (
                      <Option key={station.id} value={station.name}>
                        {station.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="displayLabel"
              label="Display Label"
              rules={[{ required: true, message: 'Please enter display label' }]}
            >
              <Input placeholder="Short label for device header (e.g., 'GRILL 1')" />
            </Form.Item>

            <Form.Item
              name="displayColor"
              label="Display Color"
            >
              {/* <ColorPicker 
                showText 
                format="hex"
                disabledAlpha
              /> */}
            </Form.Item>

            <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
              <Space>
                <Button onClick={resetForm}>
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit">
                  {editingDevice ? 'Update Device' : 'Register Device'}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </Layout>
  );
};

export default KdsDeviceManagement;
